// Commitment cancel flow (DEV-35727) — the one place that decides *where to go next* for a
// commitment plan, and *what to offer* on its screens. Plain functions over the domain
// `Subscription`, no Vue/Pinia: views and the router guard call them with what the stores already
// hold. Non-commitment plans never reach this module's routes — the existing `/cancel/*` chain is
// untouched for them. Map of the flow: docs/architecture/routing.md → "Commitment cancel flow".
import {
	SHIPPED_SUPPLY_STATES,
	type CancellationOption,
	type CancellationScope,
	type FeeInstallment,
	type Phase,
	type Subscription,
} from '@/api/medBilling'

export const COMMITMENT_ROUTES = {
	change: '/cancel/commitment/change',
	options: '/cancel/commitment/options',
	endNow: '/cancel/commitment/end-now',
	endAfter: '/cancel/commitment/end-after',
	failed: '/cancel/commitment/failed',
	refund: '/cancel/commitment/refund',
} as const

/**
 * Which commitment the plan is, as far as cancelling goes — billing's `supply_schedule.term_type`
 * (med-billing-engine#319, DEV-36310):
 * - `monthly` — `commitment`: paid every period of a locked cycle, early exit priced by a fee;
 * - `upfront` — `upfront`: the period paid in full, early exit refunds what hasn't shipped.
 * `regular` (the legacy 12w/24w plans too) or no schedule → not a commitment: the classic flow.
 * A commitment always carries the schedule (owner, 2026-10-21) — no fallback for its absence.
 */
export type CommitmentKind = 'monthly' | 'upfront'

export function commitmentKind(sub: Subscription | null): CommitmentKind | null {
	const type = sub?.supply_schedule?.term_type
	if (type === 'commitment') return 'monthly'
	if (type === 'upfront') return 'upfront'
	return null
}

/**
 * Where a commitment plan is in its life, as far as cancelling goes:
 * - `review` — the prescription is still under clinical review (Figma: "approved & shipped? → no");
 * - `last_month` — the last paid period of the commitment term, nothing left to buy out;
 * - `mid_term` — approved, with payments still owed: the screen of exit options.
 */
export type CommitmentStage = 'review' | 'last_month' | 'mid_term'

export interface StageInput {
	kind: CommitmentKind | null
	/** The store's `isRxUnderReview` (already filtered to billing statuses). */
	rxUnderReview: boolean
	/** Approved before: the order in review is a refill or a re-Rx, not the first prescription — `pastFirstRx`. */
	pastFirstRx: boolean
	remainIterations: number | null | undefined
}

/**
 * The plan's first prescription was approved, so an order in review now is a later one: a renewal that
 * ships a supply puts the plan back in `awaiting_approval` with a new order (payments 4 / 7 / 10 of the
 * 12-month plan), and a paid-in-full plan orders a mid-cycle re-Rx. Nothing on the order says which,
 * but the plan does: a period past the first (`billing_period_number`, counted over the cycles), or a
 * supply out of the pharmacy.
 */
export function pastFirstRx(sub: Subscription | null): boolean {
	return (sub?.billing_period_number ?? 1) > 1 || (sub?.supply_schedule?.supplies_shipped ?? 0) > 0
}

/**
 * TODO(DEV-35727): the Figma fork is "approved **and** 3-month supply shipped"; the supply states are on
 * the wire now (`supply_schedule.supplies`), but an approved plan whose first supply hasn't shipped
 * still reads as `mid_term` here — the design has no screen for it.
 *
 * `review` is the first prescription only. A refill in review keeps the approved plan's exits: the
 * review branch's `hard` would take the renewal back with no fee (PR #23 review, 2026-10-23).
 *
 * A paid-in-full plan under review takes the monthly commitment's branch (owner, 2026-10-21); approved
 * it is always `mid_term` — one period, its `remain_iterations` 0 from the moment it is charged.
 */
export function commitmentStage({
	kind,
	rxUnderReview,
	pastFirstRx,
	remainIterations,
}: StageInput): CommitmentStage | null {
	if (!kind) return null
	if (rxUnderReview && !pastFirstRx) return 'review'
	if (kind === 'upfront') return 'mid_term'
	if ((remainIterations ?? 0) <= 0) return 'last_month'
	return 'mid_term'
}

/**
 * After a pay-fee whose answer was lost (a 500, a dropped connection), on a fresh read: the buy-out is
 * no longer offered and the plan is cancelled — the fee was taken. Mid-term the plan ends; after the
 * instalments it stays as it was with nothing left to pay. Either way `instant_fee` goes, and a refusal
 * to charge would have come back as a `400`, not as an unknown outcome.
 */
export function feeTaken(sub: Subscription | null): boolean {
	return Boolean(sub?.cancelled) && !sub?.cancellation_options?.instant_fee
}

/** First screen after "Manage subscription" on `/status`. */
export function entryRoute(stage: CommitmentStage): string {
	return stage === 'review' ? '/cancel/scope' : '/cancel/reasons'
}

/** After `/cancel/scope` (review only): cancel now → the confirm screen; renewals only → the survey. */
export function afterScope(scope: CancellationScope | '' | undefined): string {
	return scope === 'cancel_my_current_plan' ? '/cancel/confirm' : '/cancel/reasons'
}

export type AfterLikelihood = { kind: 'route'; to: string } | { kind: 'cancel_renewals' }

/**
 * After `/cancel/likelihood`. In the review branch the survey was the "renewals only" path, and its
 * Continue *is* the cancel (Figma has no confirm step there).
 */
export function afterLikelihood(stage: CommitmentStage): AfterLikelihood {
	return stage === 'review' ? { kind: 'cancel_renewals' } : { kind: 'route', to: COMMITMENT_ROUTES.change }
}

export type ChangeChoice = 'update_payment_method' | 'cancel_plan_now'

/** After `/cancel/commitment/change`. */
export function afterChange(choice: ChangeChoice, stage: CommitmentStage): string {
	if (choice === 'update_payment_method') return '/payment/update'
	return stage === 'last_month' ? '/cancel/confirm' : COMMITMENT_ROUTES.options
}

export interface SupplyProgress {
	/** Payments made in the current supply, 1-based. */
	paid: number
	total: number
}

/**
 * Everything the commitment screens show, read off one subscription. The exits are the backend's
 * `cancellation_options` as they are — `null` means the option is not offered, so its row/screen is
 * not shown (docs/todo/cancellation-options-contract.md).
 */
export interface CommitmentQuotes {
	currency: string
	/** Price of one payment ($199 every 4 weeks; $597 every 12 weeks on a B12 plan). */
	price: number | null
	/**
	 * The plan's price per month — one payment over the months it buys ($597 every 12 weeks = $199). What
	 * the copy calls "your $X/month rate" and what the fee breakdown counts the months paid with.
	 */
	monthlyPrice: number | null
	/** Payments in the whole term (12 for a 12-month plan billed monthly, 4 for one billed every 12 weeks). */
	iterationCount: number | null
	/** Months the term covers ("a 12-month commitment") — `termMonths`. */
	termMonths: number | null
	/** "Cancel right now" — `instant_fee`. */
	endToday: CancellationOption | null
	/** "Cancel after N scheduled payments" — `installments_fee`. */
	supplyEnd: CancellationOption | null
	/** "Keep my plan, but don't renew it" — `end_of_commitment`. */
	keep: CancellationOption | null
	/**
	 * The plain cancel at the period end — `end_of_period`: the term's last month (`/cancel/confirm`),
	 * and "Cancel after my current plan ends" on a paid-in-full plan.
	 */
	endOfPeriod: CancellationOption | null
	/** Paid in full: "Cancel right now" with the unshipped part refunded — `upfront_refund`. */
	refund: CancellationOption | null
	/** "1 / 3 paid" — the payments of the current supply (`paymentsPerSupply`); `null` when nothing is paid yet. */
	progress: SupplyProgress | null
}

function activePhase(sub: Subscription | null): Phase | undefined {
	return sub?.phases?.find((phase) => phase.is_active)
}

/**
 * Payments per supply of a monthly commitment: the cycle's payments over the supplies it ships
 * (`supply_schedule.supplies_total`) — 12 / 4 = 3 on the 12-month plan, 1 on a plan billed once per supply.
 */
export function paymentsPerSupply(sub: Subscription | null): number | null {
	const count = activePhase(sub)?.iteration_count
	const supplies = sub?.supply_schedule?.supplies_total
	return count && supplies ? Math.max(1, Math.round(count / supplies)) : null
}

/**
 * Payments made in the term so far, the charge held while its order awaits approval included — the
 * engine's `PaidPeriods`. The top-level `remain_iterations` already leaves the held period out (`PeriodsLeft`),
 * so it is `iteration_count − remain_iterations`; the phase's own count drops only at the approval, so
 * without the top-level one the held charge is added back (DEV-36624). `null` without a count.
 */
function paidInTerm(sub: Subscription | null): number | null {
	const phase = activePhase(sub)
	const count = phase?.iteration_count
	if (!count) return null
	const left =
		sub?.remain_iterations ?? (phase.remain_iterations ?? count) - (sub?.status === 'awaiting_approval' ? 1 : 0)
	return Math.min(count - left, count)
}

export function commitmentQuotes(sub: Subscription | null): CommitmentQuotes {
	const phase = activePhase(sub)
	const iterationCount = phase?.iteration_count ?? null
	const paid = paidInTerm(sub) ?? 0
	const perSupply = paymentsPerSupply(sub)
	const months = periodMonths(phase)
	const options = sub?.cancellation_options
	return {
		currency: phase?.currency ?? 'USD',
		price: phase?.price ?? null,
		monthlyPrice: phase?.price != null && months ? Math.round((phase.price / months) * 100) / 100 : null,
		iterationCount,
		termMonths: termMonths(sub),
		endToday: options?.instant_fee ?? null,
		supplyEnd: options?.installments_fee ?? null,
		keep: options?.end_of_commitment ?? null,
		endOfPeriod: options?.end_of_period ?? null,
		refund: options?.upfront_refund ?? null,
		progress: paid > 0 && perSupply ? { paid: ((paid - 1) % perSupply) + 1, total: perSupply } : null,
	}
}

/**
 * One row per supply of the term, off `supply_schedule.supplies`:
 * - `shipped` — out of the pharmacy (`SHIPPED_SUPPLY_STATES`);
 * - `paid` — fully paid: every supply of a paid-in-full plan; on a monthly commitment the ones before
 *   the supply being paid for now;
 * - `current` — the monthly supply being paid for now (its "k / n paid"), placed by the payments made.
 * Under review nothing is paid yet; supply 1 is the current one.
 */
export interface SupplyRow {
	number: number
	state: string
	shipped: boolean
	paid: boolean
	current: boolean
}

export function supplyRows(sub: Subscription | null): SupplyRow[] {
	const supplies = sub?.supply_schedule?.supplies ?? []
	const kind = commitmentKind(sub)
	const paid = paidInTerm(sub)
	const perSupply = paymentsPerSupply(sub)
	let current = -1
	if (kind === 'monthly' && paid != null && perSupply) {
		current = Math.min(Math.floor((Math.max(paid, 1) - 1) / perSupply), supplies.length - 1)
	}
	return supplies.map((supply, i) => ({
		number: supply.number,
		state: supply.state,
		shipped: SHIPPED_SUPPLY_STATES.includes(supply.state),
		paid: kind === 'upfront' || i < current,
		current: i === current,
	}))
}

/** Months one billing period of the phase spans: 4 weeks → 1, 48 weeks → 12 — for a plan without a schedule. */
function periodMonths(phase: Phase | undefined): number | null {
	if (!phase?.interval_duration) return null
	if (phase.interval_unit === 'week') return phase.interval_duration / 4
	if (phase.interval_unit === 'month') return phase.interval_duration
	return null
}

/**
 * Months the plan's term covers ("12-month plan"): `supply_schedule.term_months` — the cycle of a
 * commitment, else the paid period. Without a schedule (a plan that isn't a commitment, on a read
 * without one) counted off the active phase.
 */
export function termMonths(sub: Subscription | null): number | null {
	if (sub?.supply_schedule) return sub.supply_schedule.term_months
	const phase = activePhase(sub)
	const months = periodMonths(phase)
	return months ? (phase?.iteration_count ?? 1) * months : null
}

/**
 * The Figma line items behind a refund (6847:86881), from `upfront_refund`: "{term} months already
 * paid" − "{shipped} months received × {rate}" = "Your refund". `monthlyRate` is what the plan cost per
 * month; the screen explains the repricing only when it is below the exit rate — a renewal has no
 * discount to claw back. `null` when the option carries no breakdown.
 */
export interface RefundBreakdown {
	termMonths: number
	paid: number
	shippedMonths: number
	rate: number
	received: number
	refund: number
	monthlyRate: number
}

const cents = (amount: number) => Math.round(amount * 100) / 100

export function refundBreakdown(option: CancellationOption | null, months: number | null): RefundBreakdown | null {
	const { paid, shipped_months: shipped, exit_rate: rate, refund } = option ?? {}
	if (paid == null || shipped == null || rate == null || refund == null || !months) return null
	return {
		termMonths: months,
		paid,
		shippedMonths: shipped,
		rate,
		received: cents(shipped * rate),
		refund,
		monthlyRate: cents(paid / months),
	}
}

/**
 * The Figma line items behind a fee, from the option's `paid` / `shipped_months` / `exit_rate`:
 * - end now: "{shipped} months received × {rate}" − "{paidMonths} months already paid";
 * - end after: "{unpaid} months unpaid × {rate}" + "{paidMonths} months recalculation" (each paid
 *   month repriced from the plan price up to the ladder rate).
 * `null` when the option carries no breakdown (the rest of the instalments after a `supply_end`) or
 * the paid amount isn't a whole number of plan payments — the screens then show the total alone.
 */
export interface FeeBreakdown {
	shippedMonths: number
	rate: number
	paid: number
	paidMonths: number
	unpaidMonths: number
	recalculation: number
}

/** `monthlyPrice` is the plan's price per month (`CommitmentQuotes.monthlyPrice`): the months paid are counted with it. */
export function feeBreakdown(option: CancellationOption | null, monthlyPrice: number | null): FeeBreakdown | null {
	const shipped = option?.shipped_months
	const rate = option?.exit_rate
	const paid = option?.paid
	if (shipped == null || rate == null || paid == null || !monthlyPrice) return null
	const paidMonths = paid / monthlyPrice
	if (!Number.isInteger(paidMonths)) return null
	return {
		shippedMonths: shipped,
		rate,
		paid,
		paidMonths,
		unpaidMonths: shipped - paidMonths,
		recalculation: paidMonths * rate - paid,
	}
}

/**
 * How a commitment cancel ended, captured from the backend answer (or the option taken right before
 * it — `installments_fee` is gone after a `supply_end` cancel) and read by `/cancel/done`.
 * `null` in the cancel-flow store means "the classic receipt".
 */
export type CancelOutcome =
	| { kind: 'plan_ended'; amount: number; currency: string; accessUntil: string | null }
	| { kind: 'ending_after_payments'; installments: FeeInstallment[]; currency: string; endsOn: string | null }
	| { kind: 'renewal_off'; price: number | null; currency: string; until: string | null }
	| { kind: 'renewals_cancelled'; price: number | null; currency: string; until: string | null }
	// Paid in full: ended today with the unshipped part refunded / cancelled at the period end.
	| { kind: 'refunded'; refund: number; currency: string; accessUntil: string | null }
	| { kind: 'paid_through'; until: string | null }
